/**
 * Public conversational lead capture chatbot — Maya for Espíritu Travel.
 * Route: /chat (no auth). Mirrors the espiritutravel.com/questionnaire flow.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Bot, Send, Check, Loader2, Compass, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ChatGPT Ads — parent origin authorised to receive the `lead_created` postMessage.
// The parent page (WordPress) MUST validate `event.origin` against the Maya iframe
// origin (this app's REACT_APP_BACKEND_URL host), and this page targets the parent's
// origin explicitly to prevent leakage to arbitrary embedders.
const ADS_PARENT_ORIGIN = "https://espiritutravel.com";

const STEP_LABELS = [
  { key: "countries", label: "Countries", check: (b) => Array.isArray(b?.countries) && b.countries.length > 0 },
  { key: "travelers", label: "Travelers", check: (b) => b?.adults != null },
  { key: "when", label: "Dates", check: (b) => !!b?.when },
  { key: "from_where", label: "Origin", check: (b) => !!b?.from_where },
  { key: "interests", label: "Style", check: (b) => Array.isArray(b?.interests) && b.interests.length > 0 },
  { key: "budget", label: "Budget", check: (b) => !!b?.budget_per_person },
  { key: "contact", label: "Contact", check: (b) => !!b?.email },
];

export default function PublicChatPage() {
  const isEmbed = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("embed") === "1";
  const [sessionId, setSessionId] = useState(null);
  const [mayaAvatar, setMayaAvatar] = useState("");
  const [messages, setMessages] = useState([]);
  const [brief, setBrief] = useState({});
  const [complete, setComplete] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(true);
  const [quickReplies, setQuickReplies] = useState([]);
  const scrollRef = useRef(null);
  // ChatGPT Ads — track which lead_ids have already fired `lead_created` in this
  // page instance. sessionStorage adds a second layer that survives remounts and
  // page reloads within the same tab session (dedup per lead, not per visit).
  const firedLeadsRef = useRef(new Set());

  // Fire the `lead_created` postMessage to the parent WordPress page. Only fires
  // once per lead_id (across remounts / reloads in the same tab). Does NOT fire
  // when the backend response contains no lead_id (i.e. the lead was not saved).
  const notifyParentLeadCreated = (leadIdValue) => {
    if (!leadIdValue || typeof window === "undefined" || window.parent === window) return;
    const eventId = `maya_lead_${leadIdValue}`;
    if (firedLeadsRef.current.has(eventId)) return;
    try {
      const storageKey = `voyagemarkt_ads_fired_${eventId}`;
      if (window.sessionStorage && window.sessionStorage.getItem(storageKey)) {
        firedLeadsRef.current.add(eventId);
        return;
      }
      window.parent.postMessage(
        { type: "lead_created", source: "maya", event_id: eventId, lead_id: leadIdValue },
        ADS_PARENT_ORIGIN,
      );
      firedLeadsRef.current.add(eventId);
      if (window.sessionStorage) window.sessionStorage.setItem(storageKey, "1");
    } catch (_e) {
      // Fail-safe: never break lead capture because of a tracking hiccup.
    }
  };

  useEffect(() => {
    if (isEmbed && typeof document !== "undefined") {
      // Hide preview environment watermark inside the embed iframe
      const css = document.createElement("style");
      css.textContent = `
        #emergent-badge, a[href*="emergent.sh"], a[href*="emergent-badge"] { display: none !important; visibility: hidden !important; }
      `;
      document.head.appendChild(css);
      // The badge may be injected after our CSS — observe and remove
      const obs = new MutationObserver(() => {
        const b = document.getElementById("emergent-badge");
        if (b) b.remove();
        document.querySelectorAll('a[href*="emergent.sh"]').forEach((el) => el.remove());
      });
      obs.observe(document.body, { childList: true, subtree: true });
      // Also clean once on mount
      setTimeout(() => {
        const b = document.getElementById("emergent-badge");
        if (b) b.remove();
      }, 300);
    }
    axios.get(`${API}/widget/config`).then((r) => {
      setMayaAvatar(r.data.maya_avatar_url || "");
    }).catch(() => {});
    (async () => {
      try {
        const qs = new URLSearchParams(window.location.search);
        const payload = {
          page_url: qs.get("page_url") || null,
          page_title: qs.get("page_title") || null,
          page_referrer: qs.get("page_referrer") || null,
        };
        const r = await axios.post(`${API}/public/chat/start`, payload);
        setSessionId(r.data.session_id);
        setMessages([{ role: "assistant", content: r.data.reply }]);
        if (r.data.quick_replies && r.data.quick_replies.length) {
          setQuickReplies(r.data.quick_replies);
        }
      } catch (e) {
        setMessages([{ role: "assistant", content: "Sorry, there was a problem starting the chat. Please refresh the page or email us at hello@espiritutravel.com." }]);
      } finally {
        setStarting(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, sending]);

  const send = async (textOverride) => {
    const text = (typeof textOverride === "string" ? textOverride : input).trim();
    if (!text || !sessionId || sending) return;
    if (typeof textOverride !== "string") setInput("");
    setQuickReplies([]);  // clear chips immediately
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const r = await axios.post(`${API}/public/chat/${sessionId}/message`, { message: text });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
      setBrief(r.data.brief || {});
      setComplete(!!r.data.complete);
      setLeadId(r.data.lead_id);
      // ChatGPT Ads — notify the parent WP page ONCE per lead. The backend only
      // returns lead_id after `db.leads.insert_one` succeeds (public_chat_routes.py),
      // so a non-null value here is a reliable signal of a saved lead.
      if (r.data.lead_id) notifyParentLeadCreated(r.data.lead_id);
      if (r.data.quick_replies && r.data.quick_replies.length) {
        setQuickReplies(r.data.quick_replies);
      }
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, something went wrong. Could you repeat your message?" }]);
    } finally {
      setSending(false);
    }
  };

  const completedSteps = STEP_LABELS.filter((s) => s.check(brief)).length;
  const progress = (completedSteps / STEP_LABELS.length) * 100;

  return (
    <div className={`${isEmbed ? "h-screen overflow-hidden" : "min-h-screen"} bg-white flex items-stretch justify-center ${isEmbed ? "p-0" : "p-3 sm:p-6"}`}>
      <div className={`w-full ${isEmbed ? "max-w-none h-full" : "max-w-3xl"} flex flex-col bg-white ${isEmbed ? "" : "border border-[#E2DCD0] rounded-sm shadow-sm"} min-h-0`}>
        {/* Header */}
        <header className={`${isEmbed ? "px-4 py-3" : "px-5 py-4 sm:px-7 sm:py-5"} border-b border-[#E2DCD0] flex items-center justify-between gap-3 flex-shrink-0 bg-white`}>
          <div className="flex items-center gap-3 min-w-0">
            {mayaAvatar ? (
              <img src={mayaAvatar} alt="Maya" className={`${isEmbed ? "w-9 h-9" : "w-11 h-11"} rounded-sm object-cover shrink-0 border-2 border-[#D95D39]`} />
            ) : (
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-sm bg-[#0A1128] flex items-center justify-center shrink-0">
                <Compass className="w-5 h-5 text-[#D95D39]" strokeWidth={1.5} />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[9px] uppercase tracking-[0.25em] text-[#8A8F98]">Espíritu Travel</p>
              <h1 className={`font-display ${isEmbed ? "text-base" : "text-xl sm:text-2xl"} text-[#0A1128] leading-tight`}>Maya · Travel assistant</h1>
            </div>
          </div>
          {!isEmbed && (
            <div className="hidden sm:flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] shrink-0">
              <Sparkles className="w-3 h-3 text-[#D95D39]" />
              <span>Custom design</span>
            </div>
          )}
        </header>

        {/* Progress bar (hidden in embed mode per user preference) */}
        {!isEmbed && (
          <div className="px-5 sm:px-7 pt-4 pb-2 flex-shrink-0">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] mb-2">
              <span>Step {Math.min(completedSteps + 1, STEP_LABELS.length)} of {STEP_LABELS.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-1 bg-[#E2DCD0] rounded-sm overflow-hidden">
              <div className="h-full bg-[#D95D39] transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            <div className="hidden md:flex gap-1.5 mt-3 flex-wrap">
              {STEP_LABELS.map((s) => {
                const done = s.check(brief);
                return (
                  <span
                    key={s.key}
                    className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-sm border ${done ? "border-[#2D6A4F] text-[#2D6A4F] bg-[#2D6A4F]/5" : "border-[#E2DCD0] text-[#8A8F98]"}`}
                  >
                    {done && <Check className="w-2.5 h-2.5" />}
                    {s.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Chat area */}
        <div ref={scrollRef} className={`flex-1 overflow-y-auto ${isEmbed ? "px-4 py-3" : "px-5 sm:px-7 py-5"} space-y-3 min-h-0 bg-[#F9F8F6]`} data-testid="public-chat-messages">
          {starting && (
            <div className="text-center text-[#8A8F98] text-sm py-10">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Starting conversation...
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              {m.role === "assistant" ? (
                mayaAvatar ? (
                  <img src={mayaAvatar} alt="Maya" className="w-9 h-9 rounded-sm object-cover shrink-0 border-2 border-[#D95D39]" />
                ) : (
                  <div className="w-9 h-9 rounded-sm bg-[#0A1128] flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-white" strokeWidth={1.5} />
                  </div>
                )
              ) : (
                <div className="w-9 h-9 rounded-sm bg-[#D95D39] flex items-center justify-center shrink-0">
                  <span className="text-white text-xs font-medium">You</span>
                </div>
              )}
              <div className={`max-w-[78%] px-4 py-3 rounded-sm text-[15px] whitespace-pre-wrap leading-relaxed font-medium ${m.role === "user" ? "bg-[#0A1128] text-white" : "bg-white border border-[#E2DCD0] text-[#0A1128]"}`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex gap-3">
              {mayaAvatar ? (
                <img src={mayaAvatar} alt="Maya" className="w-9 h-9 rounded-sm object-cover border-2 border-[#D95D39]" />
              ) : (
                <div className="w-9 h-9 rounded-sm bg-[#0A1128] flex items-center justify-center"><Bot className="w-4 h-4 text-white" /></div>
              )}
              <div className="px-4 py-3 bg-[#F9F8F6] border border-[#E2DCD0] rounded-sm text-sm text-[#8A8F98] italic">
                Maya is typing<span className="inline-block w-3">...</span>
              </div>
            </div>
          )}
          {complete && leadId && (
            <div className="mx-auto max-w-md p-5 bg-[#2D6A4F]/5 border border-[#2D6A4F] rounded-sm text-center">
              <Check className="w-6 h-6 mx-auto text-[#2D6A4F] mb-2" />
              <p className="font-semibold text-[#0A1128]">Got it — we'll be in touch!</p>
              <p className="text-xs text-[#1A1F2B] mt-1 leading-relaxed">
                Within <strong>24 business hours</strong> a local travel designer will reach out by phone or to the email you shared, to refine the details of the ideal trip we'll be designing for you.
              </p>
            </div>
          )}
        </div>

        {/* Quick reply chips */}
        {quickReplies.length > 0 && !sending && !complete && (
          <div className="px-3 pt-2 pb-1 flex flex-wrap gap-2 bg-white flex-shrink-0 border-t border-[#E2DCD0]">
            {quickReplies.map((q, i) => (
              <button
                key={i}
                onClick={() => send(q)}
                data-testid={`public-chat-quick-${i}`}
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#F2EFE6] hover:bg-[#D95D39] hover:text-white border border-[#E2DCD0] text-[#0A1128] transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className={`p-2.5 sm:p-3 flex gap-2 bg-white flex-shrink-0 ${quickReplies.length > 0 && !sending && !complete ? "" : "border-t border-[#E2DCD0]"}`}>
          <input
            data-testid="public-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
            placeholder={complete ? "Conversation complete" : "Type your reply..."}
            disabled={!sessionId || sending || complete}
            className="flex-1 px-3 py-2.5 bg-white border border-[#E2DCD0] rounded-sm text-[15px] font-medium text-[#0A1128] focus:outline-none focus:border-[#0A1128] disabled:opacity-50"
          />
          <button
            data-testid="public-chat-send"
            onClick={() => send()}
            disabled={!sessionId || sending || !input.trim() || complete}
            className="px-4 bg-[#D95D39] hover:bg-[#c54e2e] disabled:bg-[#E2DCD0] disabled:cursor-not-allowed text-white rounded-sm transition-colors flex items-center justify-center"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>

        {/* Footer (hidden in embed mode) */}
        {!isEmbed && (
          <footer className="px-5 sm:px-7 py-3 border-t border-[#E2DCD0] text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] flex items-center justify-between">
            <span>Powered by Maya AI</span>
            <span>Espíritu Travel · ES · PT · IT · MA</span>
          </footer>
        )}
      </div>
    </div>
  );
}
